import { useState, useEffect, useContext, useCallback } from 'react';
import NotificationService, { Notification } from '../services/notificationService';
import { UserContext } from '../context/userContext';

export interface NotificationFilters {
    type?: string;
    unreadOnly?: boolean;
    page?: number;
    limit?: number;
}

// Multiple useNotifications() instances can be mounted at once (the header dropdown and a
// page both hold their own copy). Broadcasting this event lets every instance know its data
// is stale after another instance marks-read/clears, instead of only the instance that acted.
const NOTIFICATIONS_CHANGED_EVENT = 'gpcwms:notifications-changed';
function broadcastNotificationsChanged() {
    window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT));
}

export const useNotifications = (filters: NotificationFilters = {}) => {
    const { currentUser } = useContext(UserContext);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);

    const userId = currentUser?.id != null ? Number(currentUser.id) : null;
    const warehouseId = currentUser?.warehouse_id != null ? String(currentUser.warehouse_id) : null;
    const role = currentUser?.role ?? null;
    const { type, unreadOnly, page = 1, limit = 50 } = filters;

    const fetchNotifications = useCallback(async () => {
        if (userId == null) return;
        setLoading(true);
        try {
            const result = await NotificationService.getNotifications(userId, warehouseId, role, { type, unreadOnly, page, limit });
            setNotifications(Array.isArray(result.data) ? result.data : []);
            setTotal(result.total ?? 0);
            setTotalPages(result.totalPages ?? 1);
        } catch {
            setNotifications([]);
            setTotal(0);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, warehouseId, role, type, unreadOnly, page, limit]);

    const fetchUnreadCount = useCallback(async () => {
        if (userId == null) return;
        try {
            const count = await NotificationService.getUnreadCount(userId, warehouseId, role);
            setUnreadCount(count);
        } catch {
            setUnreadCount(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, warehouseId, role]);

    const markRead = async (notificationId: number) => {
        if (userId == null) return;
        try {
            await NotificationService.markRead(userId, notificationId);
            setNotifications(prev =>
                prev.map(n => (n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n))
            );
            setUnreadCount(c => Math.max(0, c - 1));
            broadcastNotificationsChanged();
        } catch {
            // ignore
        }
    };

    const markAllRead = async () => {
        if (userId == null) return;
        try {
            await NotificationService.markAllRead(userId, warehouseId, role);
            setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at ?? new Date().toISOString() })));
            setUnreadCount(0);
            broadcastNotificationsChanged();
        } catch {
            // ignore
        }
    };

    const clearAll = async () => {
        if (userId == null) return;
        try {
            await NotificationService.clearAll(userId, warehouseId, role);
            setNotifications([]);
            setTotal(0);
            setTotalPages(1);
            setUnreadCount(0);
            broadcastNotificationsChanged();
        } catch {
            // ignore
        }
    };

    useEffect(() => {
        if (currentUser && userId != null) {
            fetchNotifications();
            fetchUnreadCount();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.id, fetchNotifications, fetchUnreadCount]);

    useEffect(() => {
        if (userId == null) return;
        const handleChanged = () => {
            fetchNotifications();
            fetchUnreadCount();
        };
        window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, handleChanged);
        return () => window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, handleChanged);
    }, [userId, fetchNotifications, fetchUnreadCount]);

    return {
        notifications,
        total,
        totalPages,
        unreadCount,
        loading,
        fetchNotifications,
        fetchUnreadCount,
        markRead,
        markAllRead,
        clearAll,
    };
};
